import {
    TextBold,
    Add,
    Brush2,
    TextItalic,
    ListBullets,
    ListNumbers,
    QuoteUp,
    ArrowRotateRight,
    Sparkles,
    Minus,
    TextStrikethrough,
    TextUnderline,
    ArrowRotateLeft,
} from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
    DEFAULT_FONT_SIZE_PT,
    EDITOR_OVERLAY_Z_CLASSNAME,
    FONT_SIZE_OPTIONS,
    formatFontSizePt,
} from './constants';
import type { ToolbarProps } from './types';
import type { ComponentType } from 'react';

type FormatButtonProps = {
    isActive: boolean;
    onClick: () => void;
    ariaLabel: string;
    icon: ComponentType<{ className?: string }>;
};

export const FormatButton = ({ isActive, onClick, ariaLabel, icon: Icon }: FormatButtonProps) => {
    return (
        <Button
            type="button"
            variant="ghost"
            size="iconSm"
            onClick={onClick}
            className={cn(isActive && 'bg-accent text-accent-foreground')}
            aria-label={ariaLabel}
        >
            <Icon className="size-4" />
        </Button>
    );
};

const Separator = () => <div className="mx-1 h-6 w-px bg-border" />;

// `fontSize` is stored as a CSS length ("12pt"); read it back as a number so the
// trigger and the checkmark can compare against FONT_SIZE_OPTIONS. Anything that
// isn't pt (pasted px, say) parses to a number that simply matches no option.
const readActiveFontSizePt = (editor: ToolbarProps['editor']): number => {
    const raw = editor.getAttributes('textStyle').fontSize;

    if (typeof raw !== 'string') return DEFAULT_FONT_SIZE_PT;

    const parsed = Number.parseFloat(raw);

    return Number.isFinite(parsed) ? parsed : DEFAULT_FONT_SIZE_PT;
};

const FontSizeControl = ({ editor }: { editor: ToolbarProps['editor'] }) => {
    const activeSize = readActiveFontSizePt(editor);

    // Step through the same list the dropdown offers, so the two controls can't
    // disagree. A size off the list (pasted 13pt, say) steps to the neighbouring
    // option rather than snapping to the top or bottom of the range.
    const nextSizeUp = FONT_SIZE_OPTIONS.find((size) => size > activeSize);
    const nextSizeDown = [...FONT_SIZE_OPTIONS].reverse().find((size) => size < activeSize);

    const applySize = (size: number) => {
        const { empty, $from, from } = editor.state.selection;
        const chain = editor.chain().focus();

        // With nothing selected, size the whole block and put the caret back.
        // A stored mark would be the Docs behaviour, but ProseMirror clears
        // stored marks on the selection change that closing the menu causes —
        // and sizing the line is what the heading dropdown this replaces did.
        if (empty) chain.setTextSelection({ from: $from.start(), to: $from.end() });

        // The default follows the document theme, so express it as "no mark"
        // rather than writing a span that pins the size forever.
        if (size === DEFAULT_FONT_SIZE_PT) {
            chain.unsetFontSize();
        } else {
            chain.setFontSize(`${size}pt`);
        }

        if (empty) chain.setTextSelection(from);

        chain.run();
    };

    return (
        <div className="flex items-center">
            <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={() => nextSizeDown !== undefined && applySize(nextSizeDown)}
                disabled={nextSizeDown === undefined}
                aria-label="Diminuir tamanho da fonte"
            >
                <Minus className="size-4" />
            </Button>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 min-w-9 justify-center px-1 font-normal tabular-nums"
                        aria-label="Tamanho da fonte"
                    >
                        {formatFontSizePt(activeSize)}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="start"
                    className={cn(
                        EDITOR_OVERLAY_Z_CLASSNAME,
                        'max-h-72 min-w-[4.5rem] overflow-y-auto',
                    )}
                    // Radix refocuses the trigger button after the menu closes, which
                    // would undo the chain's .focus() below and swallow every keystroke.
                    onCloseAutoFocus={(event: Event) => event.preventDefault()}
                >
                    {FONT_SIZE_OPTIONS.map((size) => (
                        <DropdownMenuItem
                            key={size}
                            className={cn(
                                'tabular-nums',
                                activeSize === size && 'bg-accent text-accent-foreground',
                            )}
                            onSelect={() => applySize(size)}
                        >
                            {formatFontSizePt(size)}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={() => nextSizeUp !== undefined && applySize(nextSizeUp)}
                disabled={nextSizeUp === undefined}
                aria-label="Aumentar tamanho da fonte"
            >
                <Add className="size-4" />
            </Button>
        </div>
    );
};

export default function Toolbar({
    editor,
    hasAIContext,
    hasEditorContent,
    onOpenAIPanel,
    aiTriggerRef,
    aiUiFontStyle,
}: ToolbarProps) {
    return (
        <div className="flex shrink-0 flex-wrap items-center gap-1 border-b bg-muted/50 p-1">
            <FontSizeControl editor={editor} />

            <Separator />

            <FormatButton
                isActive={editor.isActive('bold')}
                onClick={() => editor.chain().focus().toggleBold().run()}
                ariaLabel="Negrito"
                icon={TextBold}
            />
            <FormatButton
                isActive={editor.isActive('italic')}
                onClick={() => editor.chain().focus().toggleItalic().run()}
                ariaLabel="Itálico"
                icon={TextItalic}
            />
            <FormatButton
                isActive={editor.isActive('underline')}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                ariaLabel="Sublinhado"
                icon={TextUnderline}
            />
            <FormatButton
                isActive={editor.isActive('strike')}
                onClick={() => editor.chain().focus().toggleStrike().run()}
                ariaLabel="Tachado"
                icon={TextStrikethrough}
            />
            <FormatButton
                isActive={editor.isActive('highlight')}
                onClick={() => editor.chain().focus().toggleHighlight().run()}
                ariaLabel="Destacar"
                icon={Brush2}
            />

            <Separator />

            <FormatButton
                isActive={editor.isActive('bulletList')}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                ariaLabel="Lista com marcadores"
                icon={ListBullets}
            />
            <FormatButton
                isActive={editor.isActive('orderedList')}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                ariaLabel="Lista numerada"
                icon={ListNumbers}
            />
            <FormatButton
                isActive={editor.isActive('blockquote')}
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                ariaLabel="Citação"
                icon={QuoteUp}
            />

            <div className="flex-1" />

            <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                aria-label="Desfazer"
            >
                <ArrowRotateLeft className="size-4" />
            </Button>
            <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                aria-label="Refazer"
            >
                <ArrowRotateRight className="size-4" />
            </Button>

            {hasAIContext && hasEditorContent && (
                <>
                    <Separator />
                    <Button
                        ref={aiTriggerRef}
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onOpenAIPanel}
                        className="h-8 gap-1.5 px-2 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
                        aria-label="Melhorar com IA"
                        style={aiUiFontStyle}
                    >
                        <Sparkles className="size-4" />
                        IA
                    </Button>
                </>
            )}
        </div>
    );
}
